import { create } from 'zustand'
import type { CryptoAmount, FiatAmount } from '@/types'
import type { WalletCurrency } from '@/domain'

interface SellMoneyState {
  // Mode switcher (fiat or crypto)
  isFiatMode: boolean

  // Shake animation trigger for validation
  triggerShake: boolean

  // Amounts
  cryptoAmount: CryptoAmount
  fiatAmount: FiatAmount

  // From currency (the currency being sold) - set from navigation
  fromCurrency?: WalletCurrency

  // To currency (always USDT) - set from wallet data
  toCurrency?: WalletCurrency

  // UI state for currency modal
  isCurrencyModalOpen: boolean

  // Fee calculation state
  calculatedFeeAmount?: string
  calculatedCreditAmount?: string
  calculatedTotalDebitAmount?: string
  hasErrorOnFeeCalculation: boolean
}

interface SellMoneyActions {
  // Toggle input mode
  setIsFiatMode: (isFiatMode: boolean) => void

  // Manage amounts
  setCryptoAmount: (cryptoAmount: CryptoAmount) => void
  setFiatAmount: (fiatAmount: FiatAmount) => void

  // Manage currencies
  setFromCurrency: (fromCurrency: WalletCurrency) => void
  setToCurrency: (toCurrency: WalletCurrency) => void

  // Shake animation
  shakeComponent: () => void

  // UI actions for currency modal
  setIsCurrencyModalOpen: (open: boolean) => void

  // Fee calculation
  setCalculatedFeeAmount: (amount: string) => void
  setCalculatedCreditAmount: (amount: string) => void
  setCalculatedTotalDebitAmount: (amount: string) => void
  setHasErrorOnFeeCalculation: (hasError: boolean) => void

  // Full store reset
  resetSellMoneyStore: () => void
}

type SellMoneyStore = SellMoneyState & SellMoneyActions

// Initial state
const initialState: SellMoneyState = {
  isFiatMode: false,
  triggerShake: false,
  fromCurrency: undefined,
  toCurrency: undefined,
  fiatAmount: {
    amount: '0',
    code: 'USD',
  },
  cryptoAmount: {
    amount: '0',
    code: 'USDT',
  },
  isCurrencyModalOpen: false,
  calculatedFeeAmount: undefined,
  calculatedCreditAmount: undefined,
  calculatedTotalDebitAmount: undefined,
  hasErrorOnFeeCalculation: false,
}

export const useSellMoneyStore = create<SellMoneyStore>()((set) => ({
  ...initialState,

  // Toggle input mode
  setIsFiatMode: (isFiatMode) => set({ isFiatMode }),

  // Manage amounts
  setCryptoAmount: (cryptoAmount) => set({ cryptoAmount }),
  setFiatAmount: (fiatAmount) => set({ fiatAmount }),

  // Manage currencies
  setFromCurrency: (fromCurrency) =>
    set((state) => {
      // If currency changed, reset amounts
      const currencyChanged = state.fromCurrency?.code !== fromCurrency.code

      if (currencyChanged) {
        const resetFiatAmount = { amount: '0', code: state.fiatAmount.code }
        const resetCryptoAmount = {
          amount: '0',
          code: fromCurrency.code,
        }

        return {
          fromCurrency,
          fiatAmount: resetFiatAmount,
          cryptoAmount: resetCryptoAmount,
        }
      }

      return { fromCurrency }
    }),

  setToCurrency: (toCurrency) => set({ toCurrency }),

  // Shake animation
  shakeComponent: () => {
    set({ triggerShake: true })
    setTimeout(() => set({ triggerShake: false }), 600)
  },

  // UI actions for currency modal
  setIsCurrencyModalOpen: (isCurrencyModalOpen) => set({ isCurrencyModalOpen }),

  // Fee calculation
  setCalculatedFeeAmount: (calculatedFeeAmount) => set({ calculatedFeeAmount }),
  setCalculatedCreditAmount: (calculatedCreditAmount) =>
    set({ calculatedCreditAmount }),
  setCalculatedTotalDebitAmount: (calculatedTotalDebitAmount) =>
    set({ calculatedTotalDebitAmount }),
  setHasErrorOnFeeCalculation: (hasErrorOnFeeCalculation) =>
    set({ hasErrorOnFeeCalculation }),

  // Full store reset
  resetSellMoneyStore: () =>
    set((state) => ({
      ...initialState,
      // Preserve currencies to avoid flickering
      fromCurrency: state.fromCurrency,
      toCurrency: state.toCurrency,
    })),
}))
